import type { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import baseAxios from 'axios';
import * as _ from 'lodash';

import { config } from '../config';
import { cacheUtils } from '../utils/cacheUtils';
import { LocalStorageService } from './local-storage';

const CancelToken = baseAxios.CancelToken.source();
const { isCancel } = baseAxios;
const TEST_USER_ID = '0a29eed2-2e47-40b3-8a3e-d6cf5e5c4045';
const axios = baseAxios.create();

const isTokenExpired = (expTime) => {
  try {
    return expTime <= new Date().getTime();
  } catch (err) {
    return false;
  }
};

const refreshToken = async (getAccessTokenSilently) => {
  // console.log('refreshToken', { getAccessTokenSilently });
  const token = await getAccessTokenSilently();
  LocalStorageService.setItem('ACCESS_TOKEN', token);
  const nextAccessTokenRenewTime = new Date(Date.now() + 1000 * 60 * 4).getTime(); // renew every 4 minutes
  LocalStorageService.setItem('ACCESS_TOKEN_EXPIRE', nextAccessTokenRenewTime);
  return token;
};

const onRequest = async (alert, config: AxiosRequestConfig, getAccessTokenSilently): Promise<AxiosRequestConfig> => {
  let token = LocalStorageService.getItem('ACCESS_TOKEN'); // await getAccessTokenSilently();
  const expTime = LocalStorageService.getItem('ACCESS_TOKEN_EXPIRE');
  // console.log('onRequest', { token, config, getAccessTokenSilently });
  if (isTokenExpired(expTime)) {
    try {
      console.warn('token expired, refreshing token', { token, config, getAccessTokenSilently });
      token = await refreshToken(getAccessTokenSilently);
    } catch (error) {
      console.error('token failed, hard refreshing app', error);
      alert('Unable to get access token. Automatically refreshing app momentarily...', 'error');
      setTimeout(() => {
        cacheUtils.doHardRefresh();
      }, 700);
    }
  }
  config.headers.Authorization = `Bearer ${token}`;
  return config;
};

const onRequestError = (error: AxiosError): Promise<AxiosError> => {
  // console.log('onRequestError', {error});
  return Promise.reject(error);
};
const onResponse = (response: AxiosResponse): AxiosResponse => {
  return response;
};

const retryRequest = async (
  error: AxiosError,
  showBoundary: any = null,
  getAccessTokenSilently = null,
  axios: AxiosInstance | null = null,
  logout: any = null
): Promise<AxiosError> => {
  if (!axios || !getAccessTokenSilently) {
    if (showBoundary) {
      showBoundary(error);
    }
    return Promise.reject(error);
  }
  try {
    let url = error?.request?.responseURL;
    if (!url) {
      console.warn(`Could not reliably get url from error, defaulting to error.config.baseURL ${error.config.baseURL}`);
      url = error.config.baseURL;
    }
    let retryMap = LocalStorageService.getItem('FUEL_RETRY_MAP');
    if (retryMap == null) {
      retryMap = {};
    }
    if (retryMap[url] == null || !_.isNumber(retryMap[url])) {
      retryMap[url] = 0;
    }
    let retryAttempt = retryMap[url];
    retryAttempt++;
    const MAX_RETRIES = 2;
    if (retryAttempt > MAX_RETRIES) {
      console.error(`Reached max retry attempt ${MAX_RETRIES} for url ${url}`, { retryAttempt });

      if (logout) {
        console.info(`Logging out to ${window.location.origin}`);
        logout({ returnTo: window.location.origin });
      } else if (showBoundary) {
        showBoundary(error);
      }
      return await Promise.reject(error);
    }
    console.info(`Attempting retry ${retryAttempt} for url ${url}`);
    retryMap[url] = retryAttempt;
    LocalStorageService.setItem('FUEL_RETRY_MAP', retryMap);

    // This will refresh the token which will be available to the retry
    await refreshToken(getAccessTokenSilently);

    // This will cause the original request to be retried
    // https://stackoverflow.com/questions/51563821/axios-interceptors-retry-original-request-and-access-original-promise
    return await axios.request(error.config);
  } finally {
    setTimeout(() => {
      LocalStorageService.removeItem('FUEL_RETRY_MAP');
    }, 7000);
  }
};

const onResponseError = async (
  error: AxiosError,
  showBoundary: any = null,
  getAccessTokenSilently = null,
  axios: AxiosInstance | null = null,
  logout: any = null
): Promise<AxiosError> => {
  console.error('onResponseError', { error, showBoundary, getAccessTokenSilently, axios });
  if (error.response) {
    // Access Token was expired
    if (
      error.response.status === 401 ||
      error.response.status === 400 ||
      error.response.status === 404 ||
      error.response.status === 500 ||
      error.response.status === 504 ||
      error.response.status === 409
    ) {
      return Promise.reject(error);
    }
    if (error.response.status === 403) {
      return retryRequest(error, showBoundary, getAccessTokenSilently, axios, logout);
    }
    if (showBoundary) {
      showBoundary(error);
    }
  }
  // else if (error && error.message === 'Network Error' && showBoundary && error.code !== 'ECONNABORTED') {
  //   return retryRequest(error, showBoundary, getAccessTokenSilently, axios, logout);
  //   // showBoundary(error);
  // }
  return Promise.reject(error);
};

const setupInterceptorsTo = (axiosInstance: AxiosInstance): AxiosInstance => {
  // axiosInstance.interceptors.request.use(onRequest, onRequestError);
  // axiosInstance.interceptors.response.use(onResponse, onResponseError);
  return axiosInstance;
};

const fuelPortalAxios = setupInterceptorsTo(
  baseAxios.create({
    baseURL: config.fuelPortalUrl,
  })
);
const transcodeJobManagerAxios = setupInterceptorsTo(baseAxios.create());
const feedIngestServiceAxios = setupInterceptorsTo(baseAxios.create());
const fuelMapperServiceAxios = setupInterceptorsTo(baseAxios.create());
const fuelToolsApiAxios = setupInterceptorsTo(baseAxios.create());
const destinationsServiceAxios = setupInterceptorsTo(baseAxios.create());
const billingServiceAxios = setupInterceptorsTo(baseAxios.create());
const externalNotificationServiceAxios = setupInterceptorsTo(baseAxios.create());

const setAxiosTokenInterceptor = async (alert, getAccessTokenSilently, showBoundary, logout) => {
  // console.log('setAxiosTokenInterceptor', { alert, getAccessTokenSilently, showBoundary });
  try {
    fuelPortalAxios.interceptors.request.use((config) => onRequest(alert, config, getAccessTokenSilently), onRequestError);
    transcodeJobManagerAxios.interceptors.request.use(
      (config) => onRequest(alert, config, getAccessTokenSilently),
      onRequestError
    );
    feedIngestServiceAxios.interceptors.request.use((config) => onRequest(alert, config, getAccessTokenSilently), onRequestError);
    fuelMapperServiceAxios.interceptors.request.use((config) => onRequest(alert, config, getAccessTokenSilently), onRequestError);
    fuelToolsApiAxios.interceptors.request.use((config) => onRequest(alert, config, getAccessTokenSilently), onRequestError);
    destinationsServiceAxios.interceptors.request.use(
      (config) => onRequest(alert, config, getAccessTokenSilently),
      onRequestError
    );
    billingServiceAxios.interceptors.request.use((config) => onRequest(alert, config, getAccessTokenSilently), onRequestError);
    externalNotificationServiceAxios.interceptors.request.use(
      (config) => onRequest(alert, config, getAccessTokenSilently),
      onRequestError
    );

    fuelPortalAxios.interceptors.response.use(onResponse, (e) =>
      onResponseError(e, showBoundary, getAccessTokenSilently, fuelPortalAxios, logout)
    );
    transcodeJobManagerAxios.interceptors.response.use(onResponse, (e) =>
      onResponseError(e, showBoundary, getAccessTokenSilently, transcodeJobManagerAxios, logout)
    );
    feedIngestServiceAxios.interceptors.response.use(onResponse, (e) =>
      onResponseError(e, showBoundary, getAccessTokenSilently, feedIngestServiceAxios, logout)
    );
    fuelMapperServiceAxios.interceptors.response.use(onResponse, (e) =>
      onResponseError(e, showBoundary, getAccessTokenSilently, fuelMapperServiceAxios, logout)
    );
    fuelToolsApiAxios.interceptors.response.use(onResponse, (e) =>
      onResponseError(e, showBoundary, getAccessTokenSilently, fuelToolsApiAxios, logout)
    );
    destinationsServiceAxios.interceptors.response.use(onResponse, (e) =>
      onResponseError(
        e,
        // forwards the error back to parent component instead of showing Oops page
        (err: AxiosError) => {
          if (err.response?.data) {
            err.message = `An error occurred while communicating with the Destinations Service: ${err.response?.data}`;
            throw err;
          }
        },
        getAccessTokenSilently,
        destinationsServiceAxios,
        logout
      )
    );
    externalNotificationServiceAxios.interceptors.response.use(onResponse, (e) => {
      const error = e;
      if (error.response && error.response.data) {
        return Promise.reject(error);
      }
      return onResponseError(e, showBoundary, getAccessTokenSilently, externalNotificationServiceAxios, logout);
    });
    billingServiceAxios.interceptors.response.use(onResponse, (e) => {
      const error = e;
      if (error.response && error.response.data) {
        return Promise.reject(error);
      }
      return onResponseError(e, showBoundary, getAccessTokenSilently, billingServiceAxios, logout);
    });
  } catch (error) {
    // catching the login required error which we can safely ignore here
    console.error('Unable to get access token.  Please refresh your app, and contact support if the operation persists.', error);
    alert('Unable to get access token.  Please refresh your app, and contact support if the operation persists.', 'error');
  }
};

export {
  axios,
  baseAxios,
  billingServiceAxios,
  CancelToken,
  destinationsServiceAxios,
  externalNotificationServiceAxios,
  feedIngestServiceAxios,
  fuelMapperServiceAxios,
  fuelPortalAxios,
  fuelToolsApiAxios,
  isCancel,
  setAxiosTokenInterceptor,
  transcodeJobManagerAxios,
};
